/**
 * Builds an unsent .eml draft: an HTML body with its charts inline, and the
 * report attached.
 *
 * `X-Unsent: 1` is what makes Outlook open the file as an editable draft
 * rather than a received message. To, CC and Subject are left empty on purpose:
 * the sender chooses who receives governed figures, not the app.
 */
import {
  renderEmailHtml,
  renderEmailText,
  type EmailDocument,
  type ImageBlock,
} from "./emailDocument";
import { EmailDraftError } from "./errors";

export interface EmailAttachment {
  filename: string;
  contentType: string;
  bytes: Uint8Array;
}

const LINE = "\r\n";
const PNG_DATA_URL = /^data:image\/png;base64,([A-Za-z0-9+/=\s]+)$/;

function boundary(label: string): string {
  return `----=_dmi_${label}_${crypto.randomUUID().replace(/-/g, "")}`;
}

export function base64Bytes(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let at = 0; at < bytes.length; at += chunk) {
    binary += String.fromCharCode(...bytes.subarray(at, at + chunk));
  }
  return btoa(binary);
}

function base64Text(text: string): string {
  return base64Bytes(new TextEncoder().encode(text));
}

/** RFC 2045 caps encoded lines at 76 characters. */
function wrap(encoded: string): string {
  return (encoded.match(/.{1,76}/g) ?? []).join(LINE);
}

/** RFC 2231 form, so a non-ASCII filename survives. */
function filenameParameter(filename: string): string {
  const safe = filename.replace(/[\r\n"\\]/g, "_");
  return /^[\x20-\x7e]*$/.test(safe)
    ? `filename="${safe}"`
    : `filename*=UTF-8''${encodeURIComponent(safe)}`;
}

function contentId(image: ImageBlock, index: number): string {
  const slug = image.id.replace(/[^A-Za-z0-9.-]/g, "-").slice(0, 40) || "chart";
  return `chart-${index}-${slug}@dmi.local`;
}

function inlineImages(document: EmailDocument): { image: ImageBlock; cid: string; base64: string }[] {
  const images: { image: ImageBlock; cid: string; base64: string }[] = [];
  for (const block of document.blocks) {
    if (block.kind !== "image") continue;
    const match = PNG_DATA_URL.exec(block.dataUrl.trim());
    // A chart that is not a plain PNG is left out rather than embedded as-is.
    if (!match?.[1]) continue;
    images.push({
      image: block,
      cid: contentId(block, images.length),
      base64: match[1].replace(/\s/g, ""),
    });
  }
  return images;
}

export function buildEml(
  document: EmailDocument,
  attachments: EmailAttachment[] = [],
  now: Date = new Date(),
): string {
  try {
    const images = inlineImages(document);
    const cids = new Map(images.map((item) => [item.image, item.cid]));
    const visible: EmailDocument = {
      ...document,
      blocks: document.blocks.filter(
        (block) => block.kind !== "image" || cids.has(block),
      ),
    };
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /></head><body>${renderEmailHtml(
      visible,
      (image) => `cid:${cids.get(image) ?? ""}`,
    )}</body></html>`;
    const text = renderEmailText(visible);

    const mixed = boundary("mixed");
    const related = boundary("related");
    const alternative = boundary("alt");

    const parts: string[] = [
      "MIME-Version: 1.0",
      `Date: ${now.toUTCString()}`,
      "X-Unsent: 1",
      "To: ",
      "Subject: ",
      `Content-Type: multipart/mixed; boundary="${mixed}"`,
      "",
      `--${mixed}`,
      `Content-Type: multipart/related; boundary="${related}"`,
      "",
      `--${related}`,
      `Content-Type: multipart/alternative; boundary="${alternative}"`,
      "",
      `--${alternative}`,
      "Content-Type: text/plain; charset=utf-8",
      "Content-Transfer-Encoding: base64",
      "",
      wrap(base64Text(text)),
      `--${alternative}`,
      "Content-Type: text/html; charset=utf-8",
      "Content-Transfer-Encoding: base64",
      "",
      wrap(base64Text(html)),
      `--${alternative}--`,
    ];
    for (const item of images) {
      parts.push(
        `--${related}`,
        "Content-Type: image/png",
        "Content-Transfer-Encoding: base64",
        `Content-ID: <${item.cid}>`,
        `Content-Disposition: inline; ${filenameParameter(`${item.cid.split("@")[0]}.png`)}`,
        "",
        wrap(item.base64),
      );
    }
    parts.push(`--${related}--`);
    for (const attachment of attachments) {
      parts.push(
        `--${mixed}`,
        `Content-Type: ${attachment.contentType}`,
        "Content-Transfer-Encoding: base64",
        `Content-Disposition: attachment; ${filenameParameter(attachment.filename)}`,
        "",
        wrap(base64Bytes(attachment.bytes)),
      );
    }
    parts.push(`--${mixed}--`, "");
    return parts.join(LINE);
  } catch (cause) {
    throw new EmailDraftError(
      "draft_build_failed",
      "The email draft could not be prepared.",
      cause,
    );
  }
}

/** A filesystem-safe name for the downloaded draft or attachment. */
export function safeFilename(title: string, extension: string): string {
  const stem =
    title
      .replace(/[\\/:*?"<>|\r\n\t]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 80) || "DMI report";
  return `${stem}.${extension}`;
}
